/**
 * UnplacedAircraftDialog Component
 * Displays decision logs for unplaced aircraft at a specific time bucket
 */

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import * as React from "react";
import { bucketToTimeString } from "../bitmap";
import { LogEntry, ParkingRequest } from "../types";
import { DecisionLogDisplay } from "./DecisionLogDisplay";

interface UnplacedAircraftDialogProps {
  open: boolean;
  onClose: () => void;
  unplacedAircraft: ParkingRequest[];
  bucket: number;
  log: LogEntry[];
  spotIdToName?: Record<string, string>;
  startTime?: Date;
}

export const UnplacedAircraftDialog: React.FC<UnplacedAircraftDialogProps> =
  React.memo(
    ({
      open,
      onClose,
      unplacedAircraft,
      bucket,
      log,
      spotIdToName = {},
      startTime = new Date(new Date().setHours(0, 0, 0, 0)),
    }) => {
      return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="h6" component="div">
                  Unplaced Aircraft at {bucketToTimeString(bucket, startTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {unplacedAircraft.length} aircraft could not be placed at this
                  time
                </Typography>
              </Box>
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ maxHeight: "70vh", overflow: "auto" }}>
              {unplacedAircraft.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No unplaced aircraft at this time
                </Typography>
              ) : (
                unplacedAircraft.map((aircraft) => (
                  <Box
                    key={aircraft.id}
                    sx={{
                      mb: 3,
                      pb: 3,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": {
                        borderBottom: "none",
                        mb: 0,
                        pb: 0,
                      },
                    }}
                  >
                    <DecisionLogDisplay
                      aircraft={aircraft}
                      log={log}
                      spotIdToName={spotIdToName}
                    />
                  </Box>
                ))
              )}
            </Box>
          </DialogContent>
        </Dialog>
      );
    }
  );
